<template>
  <v-app>
      <router-view></router-view>
      <message ref="message" />
  </v-app>
</template>
<script>
import VueRouter from 'vue-router'
import { auth } from '@/firebase'
import { reportError } from '@/components/report'
import P from '@/components/people'
import userDirectory from '@/components/userDirectory'
import proposalService from '@/components/proposalService'
import homeScreen from '@/components/homeScreen'
import M from '@/components/menuRoutes'

// The redirects below race router.beforeEach, which may itself redirect or stop
// the same navigation -- a navigation failure there is the guard doing its job,
// not an error. Anything else (a page chunk that failed to download) is.
function navigationFailed (err) {
  if (!VueRouter.isNavigationFailure(err)) reportError('Navigation failed', err)
}
import message from '@/components/message.vue'
export default {
  components: {message},
  data(){
    return{
      unsubDirectory: null,
      unsubProposals: null
    }
  },
  watch: {
    // Keeps the page's manifest the one for the app this route belongs to, so
    // "Add to Home Screen" on a shop's phone makes a shop's icon, not คนขับ.
    '$route': {
      immediate: true,
      handler(route) {
        homeScreen.apply(document, route.path)
      }
    }
  },
  mounted(){
    // Look the component up when the dialog is actually opened, rather than
    // capturing its bound open() here. Vue binds methods to the instance they
    // came from, so anything that replaces the <message> child -- a hot reload
    // during development is the usual way -- would leave this pointing at the
    // destroyed instance: every dialog silently renders nothing, and every
    // `await this.$root.$message(...)` hangs, so the code after the await
    // never runs. That failure is invisible, which makes it expensive.
    this.$root.$message = (...args) => this.$refs.message.open(...args);
  },
  created() {
    // Initialize auth state before any component loads for persistant vuex implementation
    auth.onAuthStateChanged(async (user) => {
      if (user) {
        try {
          const idTokenResult = await user.getIdTokenResult()
          const userStatus = idTokenResult.claims.status || 'user'
          
          // Set role data
          this.$store.commit('setRole', userStatus)
          this.watchProposals(userStatus)
          
          // A driver signs in with a custom token and has NO email. Falling back
          // to the uid matters: router.beforeEach treats a falsy store.state.user
          // as "not logged in" and would bounce every driver back to the login
          // screen the moment they signed in.
          const identity = user.email || user.uid

          // Set user data
          this.$store.commit('setUser', identity)

          // keep user and role to sessionStorage
          sessionStorage.setItem('user', identity)
          sessionStorage.setItem('status', userStatus)

          this.watchDirectory(user)

          // this.$router.currentRoute.name !== 'index' && this.$router.push({ name: 'index' })

          // The commits above are deliberately NOT deferred: router.beforeEach
          // reads store.state.user, so delaying them would make the guard treat
          // a signed-in user as logged out. Only the navigation waits.
          this.afterRouterReady(() => {
            const route = this.$router.currentRoute

            // Not redirct to index if current route is for printing
            if (route.meta && route.meta.forPrinting) return

            // A driver's home is the car picker, not index -- index draws no
            // cards for the driver role. Already on a driver screen means stay
            // there.
            //
            // Match '/driver' itself or anything under '/driver/' -- a bare
            // indexOf('/driver') !== 0 test would also match a future staff
            // page like '/driverreport' and misroute it.
            if (userStatus === 'driver') {
              const currentPath = String(route.path)
              if (currentPath !== '/driver' && currentPath.indexOf('/driver/') !== 0) {
                this.$router.push({ name: 'driverCar' }).catch(navigationFailed)
              }
              return
            }

            // After login, the page the user asked for before the guard sent
            // them here (a kioskonlineshop deep link, a bookmark), else index.
            const redirect = route.name === 'login' && M.safeRedirect(route.query.redirect)
            if (redirect) {
              this.$router.replace(redirect).catch(navigationFailed)
            } else if (route.name !== 'index') {
              this.$router.push({ name: 'index' }).catch(navigationFailed)
            }
          })
        } catch (error) {
          reportError('Auth verification failed', error)
          this.clearAuthState()
        }
      } else {
        this.clearAuthState()
      }
    })
  },
  methods: {
    // vue-router resolves its FIRST navigation asynchronously, and on a cold
    // load that means a network fetch: every route component is a lazy import()
    // (see router.js), so the chunk has to arrive before $route stops being
    // vue-router's START route -- path '/', name null, meta {}.
    //
    // Firebase resolves auth from local IndexedDB and touches no network, so
    // this listener regularly WINS that race on a first visit. Reading $route
    // then does not say where the user is, it says '/'. That is what sent a
    // driver opening /#/driver to the staff login screen -- on their first ever
    // open and never again once the chunk was cached, which is why it looked
    // random rather than broken.
    //
    // onReady fires as soon as the initial navigation resolves, and fires
    // immediately if it already has, so nothing changes once the app is running
    // -- including driverBar.logout()'s own push, which still races this and
    // still ends on the PIN pad either way.
    afterRouterReady(fn) {
      this.$router.onReady(fn, fn)
    },

    // Names for every page, and the signed-in account's own emails for the
    // ownership checks (people.ownedBy). Needs no route, so it is not deferred.
    watchDirectory(user) {
      this.stopDirectory()
      this.unsubDirectory = userDirectory.subscribe(profiles => {
        this.$store.commit('setProfiles', profiles)
        this.$store.commit('setDirectory', P.buildDirectory(profiles))
        const own = profiles.find(p => p.uid === user.uid) || null
        this.$store.commit('setIdentities', P.identitiesOf(own, user.email || user.uid))
      }, err => reportError('userProfile subscription failed', err))
    },

    stopDirectory() {
      if (this.unsubDirectory) this.unsubDirectory()
      this.unsubDirectory = null
    },

    // Open propose-and-confirm requests, for the badge, the panel and the
    // planner block. Never for a driver: the driver app has none of those.
    watchProposals(role) {
      this.stopProposals()
      if (!role || role === 'driver') return
      this.unsubProposals = proposalService.subscribeOpen(
        rows => this.$store.commit('setProposals', rows),
        err => reportError('proposal subscription failed', err))
    },

    stopProposals() {
      if (this.unsubProposals) this.unsubProposals()
      this.unsubProposals = null
    },

    clearAuthState() {

      this.stopProposals()
      this.stopDirectory()
      this.$store.commit('clearDirectory')

      // Clear Vuex store
      this.$store.commit('clearUser')
      this.$store.commit('clearRole')

      // Clear session storage
      sessionStorage.removeItem('user')
      sessionStorage.removeItem('status')

      this.afterRouterReady(() => {
        // driverBar.logout() awaits signOutDriver() and then pushes driverPin
        // itself, racing this listener's own push -- auth.signOut() triggers
        // onAuthStateChanged(null), which lands here. Whichever push wins, a
        // driver must end up on the PIN pad, not the staff login screen: send a
        // driver route here to driverPin instead of login.
        //
        // Match '/driver' itself or anything under '/driver/' -- a bare
        // indexOf('/driver') !== 0 test would also match a future staff page
        // like '/driverreport' and misroute it.
        const path = String(this.$route.path)
        const onDriverRoute = path === '/driver' || path.indexOf('/driver/') === 0
        const targetName = onDriverRoute ? 'driverPin' : 'login'

        // Only redirect if not already there
        if (this.$route.name !== targetName) {
          this.$router.push({ name: targetName }).catch(navigationFailed)
        }
      })
    }
    
  }
}
</script>
<style scoped>
.background{
  background-color: #FF9800;
}
.bgV-list{
  background-color: #FFE0B2;
}
.fontV-list{
  color: #FFCC80;
}
</style>